/**
 * Program entries — V106-1-8 / V106-5-6 (migration 542, ADR-0078 §9 note,
 * ADR-0080 §11 note).
 *
 * The facts the SOC 2 and ISO 42001 screens record that have no record of
 * their own. One append-only table (`program_entries`), one validated shape
 * per kind (below), ended once; a correction is a new row after ending the old.
 *
 *   objective_ticket        subject = objective id; { ticket_key, url? }       → the Not met point is "in progress"
 *   objective_risk_request  subject = objective id; { reason (≥ 20), period_id?, expires_on? } → "in progress" until an admin decides
 *   objective_risk_decision subject = the request's entry id; { decision, note? } → owner / admin who didn't ask
 *                           (409 sod); declined ends the request (the point is Not met again).
 *                           D-106-20: accepted also writes the Risk acceptances register row (risk-acceptance-service,
 *                           same batch; migration 543 links it back); re-accepting writes no second row. Closing or
 *                           expiring that row ends the request; withdrawing (ending) an accepted request, or declining
 *                           it after an acceptance, closes its row in the same batch; a request whose register rows are all out of force
 *                           reads as lapsed (not in progress; hidden with `hideLapsedRisk`).
 *   ask                     subject = user id asked; { about }                 → "Asked <name> · <date>"
 *   expected_report_date    subject = audit period id; { date, note? }         → one live row per period
 *   scope_change_agreement  subject = audit period id; { firm, agreed_on, categories }
 *   cb_stage1               subject = 'cycle:<n>'; { occurred_on, outcome, concerns[] }
 *   cb_results              subject = 'cycle:<n>:<audit>'; { occurred_on, conform_count, nonconformities[] } + PDF
 *   cb_decision             subject = 'cycle:<n>'; { decision, occurred_on, valid_until? } + PDF; needs cb_results first (D7)
 *   cb_concern_answer       subject = '<stage1 entry id>:<concern key>'; { note? } + file
 *   cycle_booking           subject = 'cycle:<n>:<event>'; { booked_on }                → "Booked" on the timeline
 *   soa_send_back           subject = SoA version id; { reason }
 *   engine_switch_ack       subject = rule-set id; {}                          → the first one counts (D6)
 *
 * Authorization is the route's (program-entries.ts); this service checks the
 * shapes, the program's features (an ISO-only kind on SOC 2 is invalid) and
 * the per-kind rules (single live row, decision after results).
 */
import { z } from 'zod';
import { getProgramManifest } from '../programs/manifests.js';
import { AcceptanceValidationError, assertAcceptanceTerm, defaultAcceptanceExpiry } from './acceptance-service.js';
import { MIN_RATIONALE_LEN, RiskAcceptanceForbiddenError, RiskAcceptanceValidationError, closeProgramRequestAcceptancesStatement, emitAcceptanceCreated, emitAcceptancesClosed, openProgramRequestAcceptances, prepareAcceptance, } from './risk-acceptance-service.js';
export const PROGRAM_ENTRY_KINDS = [
    'objective_ticket', 'objective_risk_request', 'ask', 'expected_report_date', 'scope_change_agreement',
    'cb_stage1', 'cb_results', 'cb_decision', 'cb_concern_answer', 'cycle_booking', 'soa_send_back', 'engine_switch_ack',
    'objective_risk_decision',
];
export class ProgramEntryError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'ProgramEntryError';
    }
}
const fail = (code, m) => { throw new ProgramEntryError(code, m); };
const DATE = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
const TEXT = (max) => z.string().trim().min(1).max(max);
export const CB_AUDITS = ['stage2', 'surveillance_1', 'surveillance_2', 'recertification'];
export const CB_DECISIONS = ['granted', 'refused', 'maintained', 'suspended', 'restored', 'withdrawn', 'renewed'];
/** Per-kind data shapes (strict: unknown keys are refused). */
export const PROGRAM_ENTRY_DATA = {
    objective_ticket: z.object({ ticket_key: z.string().trim().min(1).max(60), url: z.string().url().max(500).regex(/^https:\/\//, 'Use an https link').nullable().optional() }).strict(),
    // D-106-20: the reason becomes the register's justification (≥ MIN_RATIONALE_LEN, as every acceptance);
    // the period it was asked in and an optional end date (else the register's default term) go with it.
    objective_risk_request: z.object({
        reason: z.string().trim().min(MIN_RATIONALE_LEN, `Say why the risk is acceptable (at least ${MIN_RATIONALE_LEN} characters).`).max(2000),
        period_id: z.string().trim().min(1).max(120).nullable().optional(),
        expires_on: DATE.nullable().optional(),
    }).strict(),
    objective_risk_decision: z.object({ decision: z.enum(['accepted', 'declined']), note: z.string().trim().max(500).nullable().optional() }).strict(),
    ask: z.object({ about: TEXT(200) }).strict(),
    expected_report_date: z.object({ date: DATE, note: z.string().trim().max(200).nullable().optional() }).strict(),
    scope_change_agreement: z.object({ firm: TEXT(200), agreed_on: DATE, categories: z.array(z.string().max(40)).max(8) }).strict(),
    cb_stage1: z.object({
        occurred_on: DATE,
        outcome: z.enum(['proceed', 'proceed_with_concerns', 'not_ready']),
        concerns: z.array(z.object({ key: z.string().regex(/^[a-z0-9-]{1,40}$/), ref: z.string().max(40), objective_id: z.string().max(120).nullable().optional(), text: TEXT(500) }).strict()).max(50),
    }).strict(),
    cb_results: z.object({
        audit: z.enum(CB_AUDITS),
        occurred_on: DATE,
        conform_count: z.number().int().min(0).max(5000),
        nonconformities: z.array(z.object({ objective_id: z.string().min(1).max(120), grade: z.enum(['major', 'minor']) }).strict()).max(500),
    }).strict(),
    cb_decision: z.object({ decision: z.enum(CB_DECISIONS), occurred_on: DATE, valid_until: DATE.nullable().optional() }).strict(),
    cb_concern_answer: z.object({ note: z.string().trim().max(500).nullable().optional() }).strict(),
    cycle_booking: z.object({ booked_on: DATE }).strict(),
    soa_send_back: z.object({ reason: TEXT(2000) }).strict(),
    engine_switch_ack: z.object({}).strict(),
};
/** Kinds that need the report / answer file. */
export const FILE_KINDS = new Set(['cb_results', 'cb_decision', 'cb_concern_answer']);
/** Kinds whose subject holds at most one live row (a new one ends the old). */
const SINGLE_LIVE = new Set(['expected_report_date', 'cb_stage1', 'cb_results', 'cb_decision', 'cycle_booking', 'objective_ticket', 'objective_risk_request', 'soa_send_back', 'objective_risk_decision']);
/** Kinds for programs whose manifest declares a certification body (ISO 42001). */
const CB_KINDS = new Set(['cb_stage1', 'cb_results', 'cb_decision', 'cb_concern_answer', 'cycle_booking']);
const toEntry = (r) => {
    let data = {};
    try {
        data = JSON.parse(r.data_json || '{}');
    }
    catch {
        data = {};
    }
    const { data_json: _d, ...rest } = r;
    return { ...rest, data };
};
const newId = () => `pe-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
async function profileProgram(db, workspaceId, profileId) {
    const r = (await db.query(`SELECT program FROM certification_profiles WHERE profile_id = ? AND workspace_id = ? AND status = 'active' LIMIT 1`, [profileId, workspaceId])).results[0];
    if (!r)
        fail('not_found', 'Program not found.');
    return r.program;
}
/** The program's live entries (newest first), optionally of some kinds / one subject. */
export async function listProgramEntries(db, input) {
    const where = ['workspace_id = ?', 'profile_id = ?'];
    const params = [input.workspaceId, input.profileId];
    // d1-in-list: bounded — at most the 13 entry kinds
    if (input.kinds?.length) {
        where.push(`kind IN (${input.kinds.map(() => '?').join(', ')})`);
        params.push(...input.kinds);
    }
    if (input.subjectKey) {
        where.push('subject_key = ?');
        params.push(input.subjectKey);
    }
    if (!input.includeEnded)
        where.push('ended_at IS NULL');
    const r = await db.query(`SELECT * FROM program_entries WHERE ${where.join(' AND ')} ORDER BY recorded_at DESC, entry_id DESC`, params);
    const out = r.results.map(toEntry);
    if (!input.hideLapsedRisk || !out.some((e) => e.kind === 'objective_risk_request'))
        return out;
    const lapsed = await lapsedRiskRequests(db, input.workspaceId, input.profileId, input.now ?? new Date());
    if (!lapsed.size)
        return out;
    return out.filter((e) => !(e.kind === 'objective_risk_request' && lapsed.has(e.entry_id)) && !(e.kind === 'objective_risk_decision' && lapsed.has(e.subject_key ?? '')));
}
/**
 * D-106-20: live risk requests of a program whose Risk acceptances register rows
 * exist but none is in force (open and not past its end date). The register has
 * no scheduled expiry sweep, so an expired acceptance is read here; a closed one
 * already ended its request. Empty when the columns aren't on this plane yet.
 */
export async function lapsedRiskRequests(db, workspaceId, profileId, now = new Date()) {
    try {
        const rows = (await db.query(`SELECT program_entry_id, MAX(CASE WHEN closure_status = 'open' AND target_closure_at > ? THEN 1 ELSE 0 END) AS in_force
         FROM risk_acceptances WHERE workspace_id = ? AND program_profile_id = ? AND program_entry_id IS NOT NULL
        GROUP BY program_entry_id`, [now.toISOString(), workspaceId, profileId])).results;
        return new Set(rows.filter((x) => !Number(x.in_force)).map((x) => x.program_entry_id));
    }
    catch {
        return new Set();
    }
}
/** Subject checks per kind (the subject must exist in this workspace / program). */
async function checkSubject(db, workspaceId, profileId, program, kind, subject, data) {
    const need = (ok, m) => { if (!ok)
        fail('invalid', m); };
    switch (kind) {
        case 'objective_ticket':
        case 'objective_risk_request': {
            const m = getProgramManifest(program);
            need(!!subject && !!m?.frameworkId && subject.startsWith(`${m.frameworkId}:`), 'subject_key must be an objective of this program.');
            if (kind === 'objective_risk_request' && data.period_id) {
                const r = (await db.query(`SELECT 1 AS ok FROM audit_periods WHERE period_id = ? AND workspace_id = ? AND profile_id = ? LIMIT 1`, [data.period_id, workspaceId, profileId])).results[0];
                if (!r)
                    fail('not_found', 'Period not found.');
            }
            if (kind === 'objective_risk_request' && data.expires_on) {
                try {
                    assertAcceptanceTerm(`${String(data.expires_on)}T23:59:59.000Z`, new Date(), 'expires_on');
                }
                catch (e) {
                    if (e instanceof AcceptanceValidationError)
                        fail('invalid', e.message);
                    throw e;
                }
            }
            return;
        }
        case 'ask': {
            const r = (await db.query(`SELECT 1 AS ok FROM memberships WHERE workspace_id = ? AND user_id = ? AND removed_at IS NULL LIMIT 1`, [workspaceId, subject ?? ''])).results[0];
            need(!!r, 'Ask an active member of this workspace.');
            return;
        }
        case 'expected_report_date':
        case 'scope_change_agreement': {
            const r = (await db.query(`SELECT 1 AS ok FROM audit_periods WHERE period_id = ? AND workspace_id = ? AND profile_id = ? LIMIT 1`, [subject ?? '', workspaceId, profileId])).results[0];
            if (!r)
                fail('not_found', 'Period not found.');
            return;
        }
        case 'cb_stage1':
        case 'cb_decision':
            need(!!subject && /^cycle:\d{1,2}$/.test(subject), "subject_key must be 'cycle:<n>'.");
            return;
        case 'cycle_booking':
            need(!!subject && /^cycle:\d{1,2}:(stage1|stage2|surveillance_\d|recertification)$/.test(subject), "subject_key must be 'cycle:<n>:<event>'.");
            return;
        case 'cb_results':
            need(!!subject && subject === `${(subject.split(':').slice(0, 2)).join(':')}:${String(data.audit)}` && /^cycle:\d{1,2}:/.test(subject), "subject_key must be 'cycle:<n>:<audit>' matching data.audit.");
            return;
        case 'cb_concern_answer': {
            const [entryId, key] = (subject ?? '').split(':');
            const parent = (await db.query(`SELECT * FROM program_entries WHERE entry_id = ? AND workspace_id = ? AND profile_id = ? AND kind = 'cb_stage1' AND ended_at IS NULL LIMIT 1`, [entryId ?? '', workspaceId, profileId])).results[0];
            if (!parent)
                fail('not_found', 'Stage 1 results not found.');
            const concerns = toEntry(parent).data.concerns ?? [];
            need(concerns.some((c) => c.key === key), 'No such concern in the stage 1 results.');
            return;
        }
        case 'soa_send_back': {
            const r = (await db.query(`SELECT status FROM soa_versions WHERE soa_version_id = ? AND workspace_id = ? AND profile_id = ? LIMIT 1`, [subject ?? '', workspaceId, profileId])).results[0];
            if (!r)
                fail('not_found', 'SoA version not found.');
            need(r.status === 'proposed', 'Only a proposed version can be sent back.');
            return;
        }
        case 'objective_risk_decision': {
            const r = (await db.query(`SELECT 1 AS ok FROM program_entries WHERE entry_id = ? AND workspace_id = ? AND profile_id = ? AND kind = 'objective_risk_request' AND ended_at IS NULL LIMIT 1`, [subject ?? '', workspaceId, profileId])).results[0];
            if (!r)
                fail('not_found', 'Risk acceptance request not found.');
            return;
        }
        case 'engine_switch_ack': {
            const r = (await db.query(`SELECT 1 AS ok FROM program_rule_sets WHERE rule_set_id = ? AND program_key = ? LIMIT 1`, [subject ?? '', program])).results[0];
            need(!!r, 'subject_key must be an approved rule set of this program.');
            return;
        }
    }
}
/** D-106-20: the program's open period the request was made in (else the latest started, not closed). */
async function requestPeriod(db, workspaceId, profileId, data, at) {
    if (typeof data.period_id === 'string' && data.period_id) {
        const r = (await db.query(`SELECT period_id FROM audit_periods WHERE period_id = ? AND workspace_id = ? AND profile_id = ? LIMIT 1`, [data.period_id, workspaceId, profileId])).results[0];
        if (r)
            return r.period_id;
    }
    const r = (await db.query(`SELECT period_id FROM audit_periods WHERE workspace_id = ? AND profile_id = ? AND status <> 'closed' AND period_start <= ?
      ORDER BY period_start DESC, period_id LIMIT 1`, [workspaceId, profileId, at.slice(0, 10)])).results[0];
    return r?.period_id ?? null;
}
/** Record one entry (validated); single-valued kinds end the subject's live row first. */
export async function recordProgramEntry(db, input) {
    if (!PROGRAM_ENTRY_KINDS.includes(input.kind))
        fail('invalid', 'Unknown entry kind.');
    if (input.recordedBy.startsWith('system:'))
        fail('invalid', 'Entries are recorded by a person.');
    const program = await profileProgram(db, input.workspaceId, input.profileId);
    const m = getProgramManifest(program);
    if (CB_KINDS.has(input.kind) && !m?.assessorTypes.includes('certification_body'))
        fail('invalid', 'This program has no certification body.');
    if (input.kind === 'engine_switch_ack' && !m?.readiness.switchRuleSet)
        fail('invalid', 'This program has no readiness switch.');
    if ((input.kind === 'expected_report_date' || input.kind === 'scope_change_agreement') && program !== 'soc2')
        fail('invalid', 'SOC 2 only.');
    if (input.kind === 'soa_send_back' && !m?.soa)
        fail('invalid', 'This program has no Statement of Applicability.');
    const parsed = PROGRAM_ENTRY_DATA[input.kind].safeParse(input.data ?? {});
    if (!parsed.success)
        fail('invalid', parsed.error.issues.map((i) => `${i.path.join('.') || 'data'}: ${i.message}`).join('; '));
    const data = parsed.data;
    const subject = input.subjectKey ? input.subjectKey.trim() || null : null;
    if (FILE_KINDS.has(input.kind)) {
        if (!input.artifact)
            fail('invalid', 'Attach the file.');
    }
    else if (input.artifact)
        fail('invalid', 'This entry takes no file.');
    if (input.artifact && !/^[a-f0-9]{64}$/.test(input.artifact.sha256))
        fail('invalid', 'The file has no verified content hash.');
    await checkSubject(input.contentDb ?? db, input.workspaceId, input.profileId, program, input.kind, subject, data);
    if (input.kind === 'cb_decision') {
        // D7: a decision is entered only after the audit's results exist (stage 2 for the first cycle).
        const results = await listProgramEntries(db, { workspaceId: input.workspaceId, profileId: input.profileId, kinds: ['cb_results'] });
        if (!results.some((r) => (r.subject_key ?? '').startsWith(`${subject}:`)))
            fail('conflict', 'Enter the audit results first.');
    }
    if (input.kind === 'engine_switch_ack') {
        // The first "Got it" counts; later ones change nothing.
        const prior = await listProgramEntries(db, { workspaceId: input.workspaceId, profileId: input.profileId, kinds: ['engine_switch_ack'], subjectKey: subject });
        if (prior.length)
            return { entry: prior.at(-1), ended: null };
    }
    let request;
    if (input.kind === 'objective_risk_decision') {
        // Separation of duties: whoever asked for the acceptance doesn't decide it.
        request = (await db.query(`SELECT * FROM program_entries WHERE entry_id = ? AND workspace_id = ? AND profile_id = ? LIMIT 1`, [subject ?? '', input.workspaceId, input.profileId])).results[0];
        if (request && request.recorded_by === input.recordedBy)
            fail('conflict', 'The person who asked for the acceptance cannot decide it.');
    }
    const now = input.now ?? new Date();
    const at = now.toISOString();
    // D-106-20: an accepted risk is also a Risk acceptances register row (one per request).
    let acceptance = null;
    const linked = request && input.kind === 'objective_risk_decision'
        ? (await db.query(`SELECT id, closure_status FROM risk_acceptances WHERE workspace_id = ? AND program_entry_id = ? ORDER BY accepted_at DESC, id DESC`, [input.workspaceId, request.entry_id])).results
        : [];
    if (request && input.kind === 'objective_risk_decision' && data.decision === 'accepted' && !linked.length) {
        acceptance = await registerAcceptance(db, input, program, request, now);
    }
    if (input.dryRun)
        return { entry: null, ended: null };
    if (acceptance && !input.audit)
        throw new Error('recordProgramEntry: an accepted risk decision needs the audit service (D-106-20).');
    let ended = null;
    const stmts = [];
    if (SINGLE_LIVE.has(input.kind) && subject) {
        const live = (await listProgramEntries(db, { workspaceId: input.workspaceId, profileId: input.profileId, kinds: [input.kind], subjectKey: subject }))[0];
        if (live) {
            stmts.push({ sql: `UPDATE program_entries SET ended_at = ?, ended_by = ?, end_reason = 'replaced' WHERE entry_id = ? AND workspace_id = ? AND ended_at IS NULL`, params: [at, input.recordedBy, live.entry_id, input.workspaceId] });
            ended = live.entry_id;
        }
    }
    const id = newId();
    stmts.push({
        sql: `INSERT INTO program_entries (entry_id, workspace_id, profile_id, kind, subject_key, data_json, artifact_id, artifact_sha256, recorded_by, recorded_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        params: [id, input.workspaceId, input.profileId, input.kind, subject, JSON.stringify(data), input.artifact?.artifact_id ?? null, input.artifact?.sha256 ?? null, input.recordedBy, at],
    });
    // A decline that overrides an earlier acceptance closes its register row, in the same batch.
    const closing = input.kind === 'objective_risk_decision' && data.decision === 'declined' && request
        ? linked.filter((l) => l.closure_status === 'open').map((l) => l.id) : [];
    if (closing.length && !input.audit)
        throw new Error('recordProgramEntry: closing a register acceptance needs the audit service (D-106-20).');
    if (input.kind === 'objective_risk_decision' && data.decision === 'declined' && request) {
        // Declined: the request ends, so the point is Not met (ranked first) again.
        if (closing.length)
            stmts.push(closeProgramRequestAcceptancesStatement(input.workspaceId, request.entry_id, at, input.recordedBy));
        stmts.push({ sql: `UPDATE program_entries SET ended_at = ?, ended_by = ?, end_reason = 'declined' WHERE entry_id = ? AND workspace_id = ? AND ended_at IS NULL`, params: [at, input.recordedBy, request.entry_id, input.workspaceId] });
    }
    // D-106-20: the decision and its register row are one unit of work.
    if (acceptance)
        stmts.push(prepareAcceptance(acceptance));
    if (stmts.length === 1)
        await db.execute(stmts[0].sql, stmts[0].params);
    else
        await db.batch(stmts);
    if (acceptance)
        await emitAcceptanceCreated(input.audit, acceptance);
    if (closing.length)
        await emitAcceptancesClosed(input.audit, input.workspaceId, closing, input.recordedBy, at, 'request_declined');
    const entry = (await db.query(`SELECT * FROM program_entries WHERE entry_id = ? AND workspace_id = ?`, [id, input.workspaceId])).results[0];
    return { entry: toEntry(entry), ended };
}
/**
 * D-106-20: the register row for an accepted request — expiry from the request (else the
 * register's default term), approver = the decider, owner = the requester, justification =
 * the request's reason, linked back to program, profile, period, point and request.
 * Validated by the register's own rules (refusals map to 422 / 409); nothing is written here.
 */
async function registerAcceptance(db, input, program, request, now) {
    const req = toEntry(request);
    const cp = (await db.query(`SELECT profile_id FROM customer_profiles WHERE workspace_id = ? ORDER BY created_at, profile_id LIMIT 1`, [input.workspaceId])).results[0];
    if (!cp)
        fail('invalid', 'This workspace has no organization profile to hold the acceptance.');
    let expires;
    try {
        expires = req.data.expires_on ? assertAcceptanceTerm(`${String(req.data.expires_on)}T23:59:59.000Z`, now, 'expires_on') : defaultAcceptanceExpiry(now);
    }
    catch (e) {
        if (e instanceof AcceptanceValidationError)
            fail('invalid', `${e.message} Ask for the acceptance again with a new end date.`);
        throw e;
    }
    const out = {
        id: `ra-${now.getTime().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
        workspace_id: input.workspaceId,
        profile_id: cp.profile_id,
        register_item_id: req.subject_key ?? '',
        owner_user_id: req.recorded_by,
        accepted_by_user_id: input.recordedBy,
        target_closure_at: expires,
        residual_risk_rationale: String(req.data.reason ?? ''),
        program_link: {
            program_key: program, profile_id: input.profileId, period_id: await requestPeriod(db, input.workspaceId, input.profileId, req.data, now.toISOString()),
            objective_id: req.subject_key ?? '', request_entry_id: req.entry_id,
        },
    };
    try {
        prepareAcceptance(out);
    }
    catch (e) {
        if (e instanceof RiskAcceptanceForbiddenError)
            fail('conflict', e.message);
        if (e instanceof RiskAcceptanceValidationError)
            fail('invalid', `${e.message} Ask for the acceptance again with a longer reason.`);
        throw e;
    }
    return out;
}
/** End a live entry (undo / withdraw); not_found for another workspace's or an ended row. */
export async function endProgramEntry(db, input) {
    const row = (await db.query(`SELECT * FROM program_entries WHERE entry_id = ? AND workspace_id = ? AND profile_id = ? AND ended_at IS NULL LIMIT 1`, [input.entryId, input.workspaceId, input.profileId])).results[0];
    if (!row)
        fail('not_found', 'Entry not found.');
    const at = (input.now ?? new Date()).toISOString();
    const end = { sql: `UPDATE program_entries SET ended_at = ?, ended_by = ?, end_reason = ? WHERE entry_id = ? AND workspace_id = ? AND ended_at IS NULL`,
        params: [at, input.endedBy, input.reason?.slice(0, 500) ?? null, input.entryId, input.workspaceId] };
    // D-106-20: withdrawing an accepted request closes its register row in the same batch (the point is Not met again).
    const closing = row.kind === 'objective_risk_request' ? await openProgramRequestAcceptances(db, input.workspaceId, input.entryId) : [];
    if (closing.length) {
        if (!input.audit)
            throw new Error('endProgramEntry: closing a register acceptance needs the audit service (D-106-20).');
        await db.batch([end, closeProgramRequestAcceptancesStatement(input.workspaceId, input.entryId, at, input.endedBy)]);
        await emitAcceptancesClosed(input.audit, input.workspaceId, closing, input.endedBy, at, 'request_withdrawn');
    }
    else {
        await db.execute(end.sql, end.params);
    }
    return toEntry((await db.query(`SELECT * FROM program_entries WHERE entry_id = ? AND workspace_id = ?`, [input.entryId, input.workspaceId])).results[0]);
}
/** Objective ids with a live ticket or risk request (the next-gap ranker's "in progress"). */
export async function objectivesInProgress(db, workspaceId, profileId, now) {
    // D-106-20: a request whose register acceptance has lapsed no longer counts (the point is Not met again).
    const rows = await listProgramEntries(db, { workspaceId, profileId, kinds: ['objective_ticket', 'objective_risk_request'], hideLapsedRisk: true, now });
    const out = new Map();
    for (const r of rows)
        if (r.subject_key && !out.has(r.subject_key))
            out.set(r.subject_key, r.kind === 'objective_ticket' ? 'ticket' : 'risk_request');
    return out;
}
//# sourceMappingURL=program-entry-service.js.map